
import * as React from 'react';
import { useDispatch } from 'react-redux';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { Box, Divider } from '@mui/material';
import { updateInventory } from '../../actions/inventory';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: 640,
    maxWidth: '92vw',
    borderRadius: 12,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2.5),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2, 2.5),
  },
}));

export default function CustomizedEditDialogs({
  open,
  row,
  onClose,
  onSave,
  onCorrectStock,
}) {
  const dispatch = useDispatch();

  const [medicineName, setMedicineName] = React.useState('');
  const [genericName, setGenericName]   = React.useState('');
  const [strength, setStrength]             = React.useState('');
  const [batchNumber, setBatchNumber]   = React.useState('');
  const [expiryDate, setExpiryDate]     = React.useState('');
  const [supplier, setSupplier]         = React.useState('');
  const [quantity, setQuantity]         = React.useState('');

  const [wholesalePrice, setWholesalePrice] = React.useState('');
  const [retailPrice, setRetailPrice]       = React.useState('');
  const [discount, setDiscount]             = React.useState('');
  const [lowStockThreshold, setLowStockThreshold] = React.useState('');
  const [criticalThreshold, setCriticalThreshold] = React.useState('');


  const itemType = row?.item?.type || 'MEDICINE';
const isMedicine = itemType === 'MEDICINE';

const tracksBatch  = true;
const tracksExpiry = isMedicine || !!row?.item?.trackExpiry;


  const nameLabel = isMedicine ? 'Medicine' : 'Product';

  React.useEffect(() => {
    if (!row) return;

    // const isoDate =
    //   row.expiryDateRaw instanceof Date && !Number.isNaN(row.expiryDateRaw.getTime())
    //     ? row.expiryDateRaw.toISOString().slice(0, 10)
    //     : (row.expiryDate || '');

    const isoDate =
  row.expiryDateRaw instanceof Date && !Number.isNaN(row.expiryDateRaw.getTime())
    ? row.expiryDateRaw.toISOString().slice(0, 10)
    : '';


    setMedicineName(row.displayName ?? row.medicineName ?? '');
    setGenericName(isMedicine ? (row.genericName ?? '') : '');

    setStrength(row.strength ?? '');
    setBatchNumber(row.batchNumber ?? '');
    setExpiryDate(isoDate);
    setSupplier(row.supplier ?? '');
    setQuantity(String(row?.quantity ?? ''));
    setLowStockThreshold(
      row.lowStockThreshold != null ? String(row.lowStockThreshold) : ''
    );
    setCriticalThreshold(
      row.criticalThreshold != null ? String(row.criticalThreshold) : ''
    );


    setWholesalePrice(
      row.wholesalePrice !== null && typeof row.wholesalePrice !== 'undefined'
        ? String(row.wholesalePrice)
        : ''
    );
    setRetailPrice(
      row.retailPrice !== null && typeof row.retailPrice !== 'undefined'
        ? String(row.retailPrice)
        : ''
    );
    setDiscount(
      row.discount !== null && typeof row.discount !== 'undefined'
        ? String(row.discount)
        : ''
    );
  }, [row, isMedicine]);

  const handleClose = () => onClose?.();

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#f3f3f6',
      '& fieldset': { border: 'none' },
    },
    '& input': {
      fontWeight: 600,
    },
  };

  const labelSx = { fontWeight: 700, fontSize: 13, color: '#374151', mb: 0.75 };

  const isValidNumber = (v) => v === '' || Number.isFinite(Number(v));

    const lowHas  = String(lowStockThreshold || '').trim() !== '';
    const critHas = String(criticalThreshold || '').trim() !== '';

    const lowNum  = Number(lowStockThreshold);
    const critNum = Number(criticalThreshold);

    const lowMinError =
      lowHas && (!Number.isFinite(lowNum) || lowNum < 2);

    const critMinError =
      critHas && (!Number.isFinite(critNum) || critNum < 1);

    // Only compare when both are present and numbers
    const critVsLowError =
      lowHas && critHas &&
      Number.isFinite(lowNum) &&
      Number.isFinite(critNum) &&
      critNum >= lowNum;

      const canSave =
    !!row?.id &&
    String(batchNumber || '').trim() && 
    (!tracksExpiry || String(expiryDate || '').trim()) &&
    isValidNumber(wholesalePrice) &&
    isValidNumber(retailPrice) &&
    isValidNumber(discount) &&
    !lowMinError &&
    !critMinError &&
    !critVsLowError;


  const handleSave = async () => {
    if (!row?.id) return;

    const updates = {
      supplier: String(supplier || '').trim(),
      wholesalePrice: wholesalePrice === '' ? null : Number(wholesalePrice),
      retailPrice: retailPrice === '' ? null : Number(retailPrice),
      discount: discount === '' ? 0 : Number(discount),
      
    };

    if (isMedicine) updates.strength = String(strength|| '').trim();
    updates.batchNo = String(batchNumber || '').trim();
    if (tracksExpiry) updates.expiryDate = expiryDate;
    const lowTrim  = String(lowStockThreshold || '').trim();
    const critTrim = String(criticalThreshold || '').trim();

    updates.lowStockThreshold  = lowTrim === '' ? null : Number(lowStockThreshold);
    updates.criticalThreshold  = critTrim === '' ? null : Number(criticalThreshold);



    try {
      await dispatch(updateInventory(row.id, updates));
      onSave?.({ id: row.id, ...updates });
      onClose?.();
    } catch (err) {
      console.error('Failed to edit inventory item:', err);
    }
  };

  // ✅ build fields dynamically so PRODUCTS don’t create empty “holes”
  const fields = [
    ...(tracksBatch ? [{
      key: 'batch',
      label: 'Batch Number',
      node: (
        <TextField
          placeholder="Enter batch number"
          fullWidth
          size="small"
          value={batchNumber}
          disabled
          // onChange={(e) => setBatchNumber(e.target.value)}
          sx={fieldSx}
        />
      ),
      md: 6,
    }] : []),

    {
      key: 'qty',
      label: 'Quantity',
      node: (
        <TextField
          type="number"
          fullWidth
          size="small"
          value={quantity}
          disabled
          sx={fieldSx}
        />
      ),
      md: 6,
    },

    ...(tracksExpiry ? [{
      key: 'expiry',
      label: 'Expiry Date',
      node: (
        <TextField
          type="date"
          fullWidth
          size="small"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          sx={fieldSx}
        />
      ),
      md: 6,
    }] : []),

    {
      key: 'supplier',
      label: 'Supplier',
      node: (
        <TextField
          placeholder="Enter supplier name"
          fullWidth
          size="small"
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
          sx={fieldSx}
        />
      ),
      md: 6,
    },

    ...(isMedicine ? [{
      key: 'strength',
      label: 'Strength',
      node: (
        <TextField
          placeholder="e.g. 500"
          fullWidth
          size="small"
          value={strength}
          onChange={(e) => setStrength(e.target.value)}
          sx={fieldSx}
        />
      ),
      md: 6,
    }] : []),

    {
      key: 'wholesale',
      label: 'Wholesale Price',
      node: (
        <TextField
          type="number"
          placeholder="Enter wholesale price"
          fullWidth
          size="small"
          value={wholesalePrice}
          onChange={(e) => setWholesalePrice(e.target.value)}
          sx={fieldSx}
        />
      ),
      md: 6,
    },

    {
      key: 'retail',
      label: 'Retail Price',
      node: (
        <TextField
          type="number"
          placeholder="Enter retail price"
          fullWidth
          size="small"
          value={retailPrice}
          onChange={(e) => setRetailPrice(e.target.value)}
          sx={fieldSx}
        />
      ),
      md: 6,
    },

    {
      key: 'discount',
      label: 'Discount %',
      node: (
        <TextField
          type="number"
          placeholder="Enter discount %"
          fullWidth
          size="small"
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
          sx={fieldSx}
        />
      ),
      md: 6,
    },
    {
  key: 'lowStockThreshold',
  label: 'Low stock alert',
  node: (
    <TextField
      type="number"
      placeholder="Minimum 2"
      fullWidth
      size="small"
      value={lowStockThreshold}
      onChange={(e) => setLowStockThreshold(e.target.value)}
      error={lowMinError}
      helperText={lowMinError ? 'Minimum is 2 units.' : ' '}
      sx={fieldSx}
    />
  ),
  md: 6,
},
{
  key: 'criticalThreshold',
  label: 'Critical alert',
  node: (
    <TextField
      type="number"
      placeholder="Minimum 1"
      fullWidth
      size="small"
      value={criticalThreshold}
      onChange={(e) => setCriticalThreshold(e.target.value)}
      error={critMinError || critVsLowError}
      helperText={
        critMinError
          ? 'Minimum is 1 unit.'
          : critVsLowError
          ? 'Must be less than low stock alert.'
          : ' '
      }
      sx={fieldSx}
    />
  ),
  md: 6,
},

  ];

  return (
    <BootstrapDialog onClose={handleClose} open={!!open} aria-labelledby="edit-inventory-dialog-title">
      <DialogTitle
        sx={{ m: 0, px: 2.5, py: 2, fontSize: '1.15rem', fontWeight: 800 }}
        id="edit-inventory-dialog-title"
      >
        Edit Details
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: 'absolute', right: 10, top: 10, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        {/* clean header card */}
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: '#f8fafc',
            border: '1px solid #e5e7eb',
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#6b7280', letterSpacing: 0.4 }}>
            {nameLabel}
          </Typography>

          <Typography sx={{ fontSize: 18, fontWeight: 900, color: '#111827', mt: 0.25 }}>
            {medicineName || '—'}
          </Typography>

          {isMedicine && (
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6b7280', mt: 0.25 }}>
              {genericName || '—'}
            </Typography>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>
            This edits details. To change quantity, use <b>Adjust Stock</b>.
          </Typography>

          {onCorrectStock && (
            <Button
              onClick={() => onCorrectStock(row)}
              size="small"
              sx={{
                textTransform: 'none',
                mt: 1,
                px: 0,
                fontWeight: 800,
                color: '#1976d2',
              }}
            >
              Adjust Stock →
            </Button>
          )}
        </Box>

        <Grid container spacing={2.25} sx={{justifyContent:'space-between'}}>
          {fields.map((f) => (
            <Grid key={f.key} item xs={12} md={f.md || 6} sx={{width:'45%'}}>
              <Typography component="label" sx={labelSx}>
                {f.label}
              </Typography>
              {f.node}
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button onClick={handleClose} startIcon={<CloseIcon />} sx={{ textTransform: 'none', fontWeight: 800 }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<SaveIcon />}
          sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 2, px: 2.5 }}
          disabled={!canSave}
        >
          Save
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
